import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PotentialSavingErrorType } from '../potential-savings-views.enum';
import { SupportModalService } from '@app/support-request/support-modal-component/support-modal.service';
import { OrganizationsService } from '@app/services/organizations.service';
import { take } from 'rxjs/operators';
import { IOrganization } from '@app/core/services/organization/organization.interface';
import { Router } from '@angular/router';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { FeatureFlag } from '@app/infrastructure/feature-flag-manager/feature-flag-decorator';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';

@Component({
	selector: 'app-potential-save-errors-modal',
	templateUrl: './potential-save-errors-modal.component.html',
	styleUrls: ['./potential-save-errors-modal.component.scss'],
})
export class PotentialSaveErrorsModalComponent implements OnInit {
	@FeatureFlag(FEATURE_FLAGS.ENABLE_NEW_ONBOARDING) enableNewOnboarding: boolean;
	public errorTitle: string;
	public errorText: string;
	public potentialSavingErrorType: typeof PotentialSavingErrorType = PotentialSavingErrorType;
	public isCloseButtonVisible: boolean = true;
	public readonly CONNECT_ACCOUNTS: 'Connect accounts' = 'Connect accounts';
	public readonly CLOSE_WINDOW: 'Close window' = 'Close window';
	public readonly CONNECT_SUPPORT: 'Contact Support' = 'Contact Support';
	public actionButtonText: 'Connect accounts' | 'Close window' | 'Contact Support';
	public readonly linkToDoc = 'https://docs.zesty.co/docs/generating-potential-savings-for-commitment-manager';
	private _isSupportFormVisible = false;

	public get isSupportFormVisible(): boolean {
		return this._isSupportFormVisible;
	}

	constructor(
		public featureFlagManagerService: FeatureFlagManagerService,
		@Inject(MAT_DIALOG_DATA) public data: string,
		private dialogRef: MatDialogRef<PotentialSaveErrorsModalComponent, boolean>,
		private supportModalService: SupportModalService,
		private organizationsService: OrganizationsService,
		private router: Router,
	) {}

	public ngOnInit(): void {
		this.setModalProps();
		if (
			this.data === PotentialSavingErrorType.GENERAL_ERROR ||
			this.data === PotentialSavingErrorType.SERVER_ERROR ||
			this.data === PotentialSavingErrorType.MISSING_PERMISSIONS
		) {
			this.setIsSupportFormVisible();
		}
	}

	public closeModal(showSupportForm?: boolean): void {
		this.dialogRef.close(!!showSupportForm);
	}

	public applyAction(): void {
		if (
			(this.data === PotentialSavingErrorType.GENERAL_ERROR ||
				this.data === PotentialSavingErrorType.SERVER_ERROR ||
				this.data === PotentialSavingErrorType.MISSING_PERMISSIONS) &&
			this.isSupportFormVisible
		) {
			this.closeModal(true);
			return;
		}
		if (this.data === PotentialSavingErrorType.MISSING_ACCOUNTS) {
			const path = this.enableNewOnboarding
				? AppRouteHelper.onboardAccount
				: `${AppRouteHelper.onboarding}/${AppRouteHelper.aws}`;
			void this.router.navigate([`/${path}`]);
		}
		this.closeModal();
	}

	private setModalProps(): void {
		if (this.data === PotentialSavingErrorType.MISSING_ACCOUNTS) {
			this.errorTitle = "We couldn't find any data";
			this.errorText =
				'We’re unable to calculate your potential savings because no accounts have been onboarded yet. Please connect your payer account to continue.';
			this.isCloseButtonVisible = true;
			this.actionButtonText = this.CONNECT_ACCOUNTS;
		}

		if (this.data === PotentialSavingErrorType.MISSING_TIME) {
			this.errorTitle = 'Insufficient data';
			this.errorText =
				'Because less than 30 days of data is available for your account, we are unable to calculate your potential savings at this time. Please log in again next month when more data is available.';
			this.isCloseButtonVisible = false;
			this.actionButtonText = this.CLOSE_WINDOW;
		}
		if (this.data === PotentialSavingErrorType.GENERAL_ERROR) {
			this.errorTitle = 'Something went wrong';
			this.errorText =
				'We could not calculate potential savings for your account because less than 30 days of data is available. Please login again next month after more data is available.';
			this.isCloseButtonVisible = true;
		}

		if (this.data === PotentialSavingErrorType.SERVER_ERROR) {
			this.errorTitle = 'Something went wrong';
			this.errorText = 'We could not calculate potential savings for your account';
			this.isCloseButtonVisible = true;
		}
		if (this.data === PotentialSavingErrorType.MISSING_PERMISSIONS) {
			this.errorTitle = 'No Report Available';
			this.errorText = '';
			this.isCloseButtonVisible = true;
		}
	}
	private setIsSupportFormVisible(): void {
		this.organizationsService
			.getCurrentUserOrganization()
			.pipe(take(1))
			.subscribe((organization: IOrganization) => {
				this._isSupportFormVisible = this.supportModalService.defineIsSupportAvailable(organization);
				this.actionButtonText = this.isSupportFormVisible ? this.CONNECT_SUPPORT : this.CLOSE_WINDOW;
			});
	}
}
